import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

export function apiErrorMessage(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((e) => e?.msg || JSON.stringify(e)).join("; ");
  }
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return fallback;
}

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("fdl_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const AuthAPI = {
  register: (email, password, name, ref) => api.post("/auth/register", { email, password, name, ref }),
  login: (email, password) => api.post("/auth/login", { email, password }),
  google: (session_id) => api.post("/auth/google", { session_id }),
  me: () => api.get("/auth/me"),
};

export const UserAPI = {
  usage: () => api.get("/user/usage"),
  trackExport: (kind, style, filename) =>
    api.post("/user/track-export", { kind, style, filename }),
};

export const TokensAPI = {
  redeem: (code) => api.post("/tokens/redeem", { code }),
  adminGenerate: (quantity, note, secret, tier = "pro") =>
    api.post("/tokens/admin/generate", { quantity, note, tier }, { headers: { "X-Admin-Secret": secret } }),
  adminList: (secret) =>
    api.get("/tokens/admin/list", { headers: { "X-Admin-Secret": secret } }),
};

export const AiAPI = {
  generate: (prompt, style = "vector", dtf_optimize = true) =>
    api.post("/ai/generate", { prompt, style, dtf_optimize }),
  edit: (image_base64, prompt, dtf_optimize = true) =>
    api.post("/ai/edit", { image_base64, prompt, dtf_optimize }),
  dtfOptimize: (image_base64) => api.post("/ai/dtf-optimize", { image_base64 }),
  watermarkRemove: (image_base64) => api.post("/ai/watermark-remove", { image_base64 }),
  photoUpgrade: (image_base64, preset = "auto", extra_prompt = "") =>
    api.post("/ai/photo-upgrade", { image_base64, preset, extra_prompt }),
};

export const ContactAPI = {
  submit: (name, email, message) => api.post("/contact/", { name, email, message }),
};

export const PaymentsAPI = {
  checkout: (lookup_key, origin_url) => api.post("/payments/checkout", { lookup_key, origin_url }),
  status: (session_id) => api.get(`/payments/status/${session_id}`),
  plans: () => api.get("/payments/plans"),
};

export const ProjectsAPI = {
  list: () => api.get("/projects"),
  get: (id) => api.get(`/projects/${id}`),
  save: (payload) => api.post("/projects", payload),
  remove: (id) => api.delete(`/projects/${id}`),
};

export const ReferralsAPI = {
  me: () => api.get("/referrals/me"),
  apply: (code) => api.post("/referrals/apply", { code }),
};

export const LeadsAPI = {
  services: () => api.get("/leads/services"),
  create: (payload) => api.post("/leads", payload),
  adminList: (secret, status) =>
    api.get("/leads/admin", { params: status ? { status } : {}, headers: { "X-Admin-Secret": secret } }),
  adminGet: (secret, id) => api.get(`/leads/admin/${id}`, { headers: { "X-Admin-Secret": secret } }),
  adminUpdate: (secret, id, payload) =>
    api.patch(`/leads/admin/${id}`, payload, { headers: { "X-Admin-Secret": secret } }),
  adminDelete: (secret, id) => api.delete(`/leads/admin/${id}`, { headers: { "X-Admin-Secret": secret } }),
};

export const AdminAPI = {
  stats: (secret) => api.get("/admin/stats", { headers: { "X-Admin-Secret": secret } }),
  users: (secret, params = {}) => api.get("/admin/users", { params, headers: { "X-Admin-Secret": secret } }),
  userGet: (secret, id) => api.get(`/admin/users/${id}`, { headers: { "X-Admin-Secret": secret } }),
  userPatch: (secret, id, payload) =>
    api.patch(`/admin/users/${id}`, payload, { headers: { "X-Admin-Secret": secret } }),
  userDelete: (secret, id) => api.delete(`/admin/users/${id}`, { headers: { "X-Admin-Secret": secret } }),
  transactions: (secret, params = {}) => api.get("/admin/transactions", { params, headers: { "X-Admin-Secret": secret } }),
};

export default api;
